import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useT } from '@/store';
import { useTenantId } from '@/hooks/useTenant';
import { api } from '@/services/api';
import { Panel, ErrorState, SkeletonRows, EmptyState } from '@/components/ui/Panel';
import { StatusBadge } from '@/components/ui/StatusBadge';

/**
 * Kas pažadėta konkrečiai dienai.
 *
 * KODĖL NE KALENDORIUS. Kalendorius yra planavimo įrankis: mėnesio tinklelis
 * atsako „kada laisva". Ryte dispečeriui reikia kito daikto — „štai kas
 * pažadėta šiandien, ir kas iš to dar neturi vairuotojo". Vienos dienos
 * sąrašas su tuo atsakymu naudingesnis nei trisdešimt langelių, kuriuose tas
 * atsakymas paslėptas.
 *
 * KODĖL DIENA, O NE `created_at`. Sąrašo `date_from`/`date_to` filtruoja
 * suvedimo laiką, tad pirmadienį suvestos septynios penktadienio mašinos
 * jais randamos pirmadienį. `?day=` klausia, kas TĄ dieną dirbama — trys
 * keliai į dieną gyvena core/order_liveness.py, ir to paties apibrėžimo
 * klausia krūvio panelė.
 *
 * NEPRISKIRTI RODOMI ATSKIRAI IR VIRŠUJE. Tai vienintelis dalykas šiame
 * ekrane, kuris reikalauja veiksmo: krovinys be vairuotojo tą dieną
 * nevažiuos, kol kas nors jo nepriskirs. Likusieji yra informacija.
 */
export function DayBoard() {
  const t = useT();
  const tenantId = useTenantId();
  const [day, setDay] = useState(() => todayLocalISO());

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['day-board', tenantId, day],
    queryFn: () => api(`/api/orders?day=${day}&limit=100`),
    enabled: Boolean(tenantId) && Boolean(day),
    // Ta pati riba, kaip krūvio panelėje: dispečeris atidaro šitą ekraną
    // priimti sprendimo, o sprendimas trunka mažiau nei minutę.
    staleTime: 60_000,
  });

  const orders = data?.orders ?? [];
  // Nepriskirti ir dar neįvykdyti — vieninteliai, dėl kurių reikia ką nors
  // daryti. Pristatytas ar atšauktas krovinys be vairuotojo yra istorija, ne
  // skyle dienoje.
  const needDriver = orders.filter(
    (o) => !o.driver_id && !['delivered', 'cancelled'].includes(o.order_status),
  );

  return (
    <Panel title={t('dayBoard.title')}>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <input
          className="field !w-auto"
          type="date"
          value={day}
          onChange={(e) => setDay(e.target.value)}
          aria-label={t('dayBoard.dayLabel')}
        />
        <button type="button" className="btn !px-2.5 !py-1 !text-[11px]"
                onClick={() => setDay(todayLocalISO())}>
          {t('dayBoard.todayBtn')}
        </button>
      </div>

      {isError ? <ErrorState error={error} /> : null}
      {isLoading ? <SkeletonRows rows={4} /> : null}

      {!isLoading && data ? (
        orders.length === 0 ? (
          <EmptyState>{t('dayBoard.nothing')}</EmptyState>
        ) : (
          <>
            <p className="mb-2 text-[11px] text-dim">
              {t('dayBoard.summary', { orders: orders.length, unassigned: needDriver.length })}
            </p>
            {needDriver.length > 0 ? (
              <p className="mb-2 rounded-[10px] border border-amber/30 bg-amber/8 px-3 py-2 text-[11px] text-amber">
                {t('dayBoard.needDriver', { count: needDriver.length })}
              </p>
            ) : null}
            <ul className="space-y-1.5">
              {/* Nepriskirti pirmi: tai vienintelė eilutė, kuri prašo
                  veiksmo, ir sąrašo apačioje ji būtų perskaityta paskutinė. */}
              {[...needDriver, ...orders.filter((o) => !needDriver.includes(o))].map((order) => (
                <li
                  key={order.order_id}
                  className={`flex flex-wrap items-baseline justify-between gap-2 rounded-[10px] border px-3 py-2 text-[11.5px] ${
                    needDriver.includes(order) ? 'border-amber/25 bg-amber/5' : 'border-line'
                  }`}
                >
                  <span className="font-semibold text-ink">
                    {order.cargo_type}
                    {order.estimated_weight_tons ? ` · ${order.estimated_weight_tons} t` : ''}
                  </span>
                  <span className="text-dim">
                    {order.site_name ?? ''}
                    {' · '}
                    {order.driver_name || (
                      <span className="text-amber">{t('dayBoard.noDriver')}</span>
                    )}
                  </span>
                  <StatusBadge status={order.order_status} />
                </li>
              ))}
            </ul>
          </>
        )
      ) : null}
    </Panel>
  );
}

/**
 * Šiandien VIETINE diena.
 *
 * toISOString() grąžina UTC, ir Vilniuje pirmas dvi-tris paros valandas jis
 * vadina vakarykštę dieną — dispečeris, atidaręs lentą 01:30, pamatytų
 * vakar. Ta pati klaidos klasė, kurią backend'as vengia laikydamas
 * `scheduled_for` vietinio laikrodžio tekstu.
 */
function todayLocalISO() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const dayOfMonth = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${dayOfMonth}`;
}
